import { CommonModule, NgIf } from '@angular/common'
import { Component, DestroyRef, inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms'
import {
  CUSTOM_DEDUCTIONS,
  CUSTOM_EARNINGS,
  REIMBURSEMENTS
} from '../../../../store/state.constants'
import { ModalsComponent } from '../../employee-salary/modals/modals.component'
import { MemoizedSelector, Store } from '@ngrx/store'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { selectSalaryComponents } from '../../../../store/salary-components/salary-components.selectors'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { BehaviorSubject, combineLatest, map, Observable, take, takeUntil, tap } from 'rxjs'
import { SalaryComponent } from '../../../../store/salary-components/salary-components.state'
import {
  ExtendedSalaryComponent,
  StatutoryCompliances
} from '../../../../store/employee-salary/employee-salary.state'
import { ActivatedRoute } from '@angular/router'
import { IndianNumberInputFormatDirective } from '../../../../directives/indian-number-input-format.directive'
import {
  CTCData,
  PayoutData,
  PayoutFrequency,
  PayOutMode
} from '../../../../store/payout-agreement/payout-agreement.state'

import {
  selectCtcData,
  selectPayoutData,
  selectRevisedCtcData,
  selectRevisedPayoutData,
  selectRevisedStatutoryCompliances,
  selectStatutoryCompliances
} from '../../../../store/payout-agreement/payout-agreement.selectors'
import {
  payoutAgreementActions,
  payoutRevisionActions
} from '../../../../store/payout-agreement/payout-agreement.actions'

@Component({
  selector: 'app-payout-data',
  imports: [NgIf, FormsModule, CommonModule, ReactiveFormsModule, IndianNumberInputFormatDirective],
  providers: [{ provide: LOCALE_ID, useValue: 'en-IN' }],
  templateUrl: './payout-data.component.html',
  styleUrl: './payout-data.component.scss'
})
export class PayoutDataComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  private modalService = inject(NgbModal)
  private fb = inject(FormBuilder)

  currentMode!: PayOutMode
  payoutMode = PayOutMode
  form!: FormGroup

  //---Defaults---//
  ctc = 0
  basicPercentage = 50 //Value in percent
  //hraPercentage = 0 //Value in percent, set in ngOnInit
  annualPfCap = 21600 // Hard coded as 1800/month in statutory compliances

  complianceSettings!: StatutoryCompliances
  payoutFrequency!: PayoutFrequency
  earnings!: SalaryComponent[]
  earnings$!: Observable<SalaryComponent[]>
  earningsTabName = CUSTOM_EARNINGS
  deductions!: SalaryComponent[]
  deductions$!: Observable<SalaryComponent[]>
  deductionsTabName = CUSTOM_DEDUCTIONS
  reimbursements!: SalaryComponent[]
  reimbursements$!: Observable<SalaryComponent[]>
  reimbursementsTabName = REIMBURSEMENTS
  OtherComponentsModal = ModalsComponent

  //---State---//
  selectedComponentItem: SalaryComponent | null = null
  employerContribution = 0
  employeeContribution = 0
  isEditing = false // Whether any earning, deduction or reimbursement is being edited in the modal
  currentStep = 1
  addCompError: string | null = null
  searchTerm = ''
  private searchTerm$ = new BehaviorSubject<string>('')
  currentCtcValue = 0
  currency = 'INR'

  // --- Filtered Observables for Modals ---
  filteredEarnings$!: Observable<SalaryComponent[]>
  filteredDeductions$!: Observable<SalaryComponent[]>
  filteredReimbursements$!: Observable<SalaryComponent[]>

  ngOnInit() {
    this.currentMode = this.route.snapshot.data['mode'] as PayOutMode
    const savedCtc = localStorage.getItem('currentPayoutCtc')
    if (savedCtc) {
      this.currentCtcValue = Number(savedCtc)
    }
    this.initializeForm()
    this.initializeComponentState()
  }

  private initializeComponentState(): void {
    this.earnings$ = this.store.select(selectSalaryComponents(CUSTOM_EARNINGS))
    this.deductions$ = this.store.select(selectSalaryComponents(CUSTOM_DEDUCTIONS))
    this.reimbursements$ = this.store.select(selectSalaryComponents(REIMBURSEMENTS))
    // --- Create filtered observables for the modals ---
    this.filteredEarnings$ = this.createFilteredObservable(this.earnings$)
    this.filteredDeductions$ = this.createFilteredObservable(this.deductions$)
    this.filteredReimbursements$ = this.createFilteredObservable(this.reimbursements$)

    let selectors: {
      compliance: MemoizedSelector<object, StatutoryCompliances | null>
      ctcData: MemoizedSelector<object, CTCData | null>
      payoutData: MemoizedSelector<object, PayoutData | null>
    }

    switch (this.currentMode) {
      case PayOutMode.CREATE:
        selectors = {
          compliance: selectStatutoryCompliances,
          ctcData: selectCtcData as MemoizedSelector<object, CTCData | null>,
          payoutData: selectPayoutData
        }
        break
      case PayOutMode.REVISE:
      case PayOutMode.UPDATE:
        selectors = {
          compliance: selectRevisedStatutoryCompliances,
          ctcData: selectRevisedCtcData as MemoizedSelector<object, CTCData | null>,
          payoutData: selectRevisedPayoutData
        }
        break
      default:
        throw new Error(`Unknown payout mode: ${this.currentMode}`)
    }

    combineLatest([
      this.store.select(selectors.compliance),
      this.store.select(selectors.ctcData),
      this.store.select(selectors.payoutData)
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(([complianceSettings, ctcData, payoutData]) => {
          if (!complianceSettings || ctcData?.ctc === undefined || ctcData?.ctc === null) {
            return
          }
          this.complianceSettings = complianceSettings
          this.ctc = Number(ctcData.ctc) ?? 0
          this.payoutFrequency = ctcData.payoutFrequency ?? ''
          this.currency = ctcData.currency ?? 'INR'

          const isPfApplicable =
            this.payoutFrequency === PayoutFrequency.MONTHLY && this.complianceSettings?.pf

          if (payoutData?.basic && this.ctc === this.currentCtcValue) {
            const processedPayoutData = this.ensureIntegerAmounts(payoutData)
            if (!isPfApplicable) {
              processedPayoutData.pf = 0
            } else {
              processedPayoutData.pf = this.calculatePf()
            }
            this.setPayoutData(processedPayoutData)
            this.recalculatePercentagesFromForm()
          } else {
            this.calculateInitialValues()
          }

          this.setupFormValueChangeListeners()
          this.form.updateValueAndValidity()
          this.calculateSpecialAllowance()
        })
      )
      .subscribe()
  }

  private createFilteredObservable(
    source$: Observable<SalaryComponent[]>
  ): Observable<SalaryComponent[]> {
    return combineLatest([source$, this.searchTerm$]).pipe(
      map(([components, term]) => {
        if (!term?.trim()) {
          return components // If no search term, return the full list
        }
        const lowerCaseTerm = term.toLowerCase()
        return components.filter(component => component.name.toLowerCase().includes(lowerCaseTerm))
      })
    )
  }

  // Helper to ensure loaded data conforms to integer requirement
  private ensureIntegerAmounts(payoutData: PayoutData): PayoutData {
    const processedData = { ...payoutData }
    processedData.basic = Math.ceil(processedData.basic ?? 0)
    //processedData.hra = Math.ceil(processedData.hra ?? 0)
    processedData.pf = Math.ceil(processedData.pf ?? 0)

    if (processedData.otherComponents) {
      processedData.otherComponents = processedData.otherComponents.map(comp => ({
        ...comp,
        employerContribution: Math.ceil(comp.employerContribution ?? 0),
        employeeContribution: Math.ceil(comp.employeeContribution ?? 0)
      }))
    }
    return processedData
  }

  private setupFormValueChangeListeners(): void {
    // --- Basic Changes ---
    this.form
      .get('basic')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newBasicValue => {
        if (!this.form.get('basic')?.valid || newBasicValue === null) return

        const ceiledNewBasic = Math.ceil(newBasicValue)
        const maxBasic = this.getMaxAllowedValue('basic')

        if (ceiledNewBasic > maxBasic) {
          this.form.get('basic')?.setValue(maxBasic, { emitEvent: false })
          this.updateBasicPercentage()
          //this.calculateHraFromBasic()
          this.calculateSpecialAllowance()
        } else {
          // Set the ceilinged value back if it changed
          if (ceiledNewBasic !== newBasicValue) {
            this.form.get('basic')?.setValue(ceiledNewBasic, { emitEvent: false })
          }
          this.updateBasicPercentage()
          //this.calculateHraFromBasic()
          this.calculateSpecialAllowance()
        }
      })

    // --- HRA Changes ---
    //this.form
    //  .get('hra')
    //  ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
    //  .subscribe(newHraValue => {
    //    if (!this.form.get('hra')?.valid || newHraValue === null) return

    //    const ceiledNewHra = Math.ceil(newHraValue)
    //    const maxHra = this.getMaxAllowedValue('hra')

    //    if (ceiledNewHra > maxHra) {
    //      this.form.get('hra')?.setValue(maxHra, { emitEvent: false })
    //      this.updateHraPercentage()
    //      this.calculateSpecialAllowance()
    //    } else {
    //      if (ceiledNewHra !== newHraValue) {
    //        this.form.get('hra')?.setValue(ceiledNewHra, { emitEvent: false })
    //      }
    //      this.updateHraPercentage()
    //      this.calculateSpecialAllowance()
    //    }
    //  })

    // --- PF Changes ---
    this.form
      .get('pf')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newPfValue => {
        if (!this.form.get('pf')?.valid || newPfValue === null) return

        const ceiledNewPf = Math.ceil(newPfValue)
        if (ceiledNewPf !== newPfValue) {
          this.form.get('pf')?.setValue(ceiledNewPf, { emitEvent: false })
        }
        this.calculateSpecialAllowance()
      })

    // --- Other Components Changes ---
    this.addedComponentItems.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calculateSpecialAllowance()
      })
  }

  ngOnDestroy() {
    if (this.form.valid) {
      // Ensure payload has integers before dispatching
      const rawValue = this.form.getRawValue()
      const finalPayload = this.ensureIntegerAmounts(rawValue)

      switch (this.currentMode) {
        case PayOutMode.CREATE:
          this.store.dispatch(payoutAgreementActions.addPayoutData({ payoutData: finalPayload }))
          break
        case PayOutMode.REVISE:
        case PayOutMode.UPDATE:
          this.store.dispatch(payoutRevisionActions.updatePayoutData({ payoutData: finalPayload }))
          break
        default:
        // TODO: Add error to Sentry or logging service
        //const errorMessage = `ngOnDestroy: Unknown or unsupported salary mode encountered: ${this.currentMode}`
        //this.sentryService.captureException(new Error(errorMessage)) // Send to Sentry
      }
    }
  }

  initializeForm() {
    this.form = this.fb.group(
      {
        basic: [0, [Validators.required, Validators.min(0)]],
        pf: [
          {
            value: 0,
            //disabled: this.payoutFrequency !== PayoutFrequency.MONTHLY
            disabled: true
          },
          [Validators.required, Validators.min(0)]
        ],
        otherComponents: this.fb.array([]),
        specialAllowance: [{ value: 0, disabled: true }, Validators.required]
      },
      { validators: this.ctcAllocationValidator() }
    )

    if (this.complianceSettings) {
      this.form.get('pf')?.addValidators(this.cappedAmountValidator.bind(this))
      this.form.get('pf')?.updateValueAndValidity()
    } else {
      // Defer or handle case where settings aren't ready
    }
  }

  // Form Level Validator (Checks sum against CTC)
  ctcAllocationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.ctc || !control) {
        return null
      }

      const formGroup = control as FormGroup
      const basic = formGroup.get('basic')?.value ?? 0
      //const hra = formGroup.get('hra')?.value ?? 0
      const pf = formGroup.get('pf')?.value ?? 0
      const otherComponentsSum = this.getOtherComponentsSumInCtc() // Already integers

      const totalAllocated = Number(basic) + Number(pf) + Number(otherComponentsSum)
      //const totalAllocated = Number(basic) + Number(hra) + Number(pf) + Number(otherComponentsSum)
      return totalAllocated > this.ctc ? { ctcExceeded: true } : null
    }
  }

  // PF Specific Capping Validator
  cappedAmountValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.complianceSettings) return null

    const maxAmount = this.complianceSettings.employerCapping
      ? Math.floor(this.annualPfCap)
      : Number.MAX_SAFE_INTEGER

    if (control.value !== null) {
      const controlValue = Math.ceil(control.value)
      if (controlValue > maxAmount) {
        control.setValue(maxAmount, { emitEvent: false })
      } else if (controlValue < 0) {
        return { negativeValue: true }
      } else if (control.value !== controlValue) {
        // If the original value had decimals, set the ceiled value back
        control.setValue(controlValue, { emitEvent: false })
      }
    }
    return null
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
      if (control.hasError('min')) {
        return 'Value cannot be negative'
      }
      if (control.hasError('negativeValue')) {
        return 'Value cannot be negative'
      }
      // Add pattern error if you add integer validation
      // if (control.hasError('pattern')) { return 'Please enter a whole number'; }
    }
    if (controlName === 'form' && this.form.hasError('ctcExceeded')) {
      // Use Math.floor on ctc for display consistency if desired
      const displayCTC = Math.floor(this.ctc)
      return `Total allocated payout components exceed the total CTC (${displayCTC}).`
    }
    return null
  }

  createOtherComponentGroup(componentData?: ExtendedSalaryComponent): FormGroup {
    return this.fb.group({
      id: [componentData?.id],
      belongsTo: [componentData?.belongsTo, Validators.required],
      name: [componentData?.name, Validators.required],
      description: [componentData?.description],
      componentType: [componentData?.componentType, Validators.required],
      info: this.fb.group({
        // ... info properties ...
        deductionPlan: [componentData?.info?.deductionPlan],
        payType: [componentData?.info?.payType],
        calculationType: [componentData?.info?.calculationType],
        paymentFrequency: [componentData?.info?.paymentFrequency],
        taxDeductionPreference: [componentData?.info?.taxDeductionPreference],
        taxable: [componentData?.info?.taxable ?? false],
        isInCTC: [componentData?.info?.isInCTC ?? false],
        isInFBP: [componentData?.info?.isInFBP ?? false],
        proRataBasis: [componentData?.info?.proRataBasis ?? false],
        EPFContribution: [componentData?.info?.EPFContribution ?? false],
        ESIContribution: [componentData?.info?.ESIContribution ?? false],
        optional: [componentData?.info?.optional ?? false]
      }),

      employerContribution: [
        Math.ceil(componentData?.employerContribution ?? 0),
        [Validators.required, Validators.min(0)]
      ],
      employeeContribution: [
        Math.ceil(componentData?.employeeContribution ?? 0),
        [Validators.required, Validators.min(0)]
      ]
    })
  }

  setPayoutData(payoutData: PayoutData): void {
    const otherComponentsArray = this.form.get('otherComponents') as FormArray<FormGroup>
    otherComponentsArray.clear({ emitEvent: false })

    if (payoutData.otherComponents && Array.isArray(payoutData.otherComponents)) {
      payoutData.otherComponents.forEach((component: ExtendedSalaryComponent) => {
        const componentGroup = this.createOtherComponentGroup(component)
        otherComponentsArray.push(componentGroup, { emitEvent: false })
      })
    }

    const { otherComponents: _, specialAllowance: _specialAllowance, ...topLevelData } = payoutData

    // Ensure top-level amounts are integers before patching
    const integerTopLevelData = {
      ...topLevelData,
      basic: Math.ceil(topLevelData.basic ?? 0),
      //hra: Math.ceil(topLevelData.hra ?? 0),
      pf: Math.ceil(topLevelData.pf ?? 0)
    }

    this.form.patchValue(integerTopLevelData, { emitEvent: false })

    this.form.updateValueAndValidity({ emitEvent: false })
    this.calculateSpecialAllowance() // Recalculate SA based on patched integer values
  }

  private recalculatePercentagesFromForm(): void {
    const basic = this.form.get('basic')?.value || 0
    //const hra = this.form.get('hra')?.value || 0

    if (this.ctc > 0) {
      this.basicPercentage = Math.round((basic / this.ctc) * 100 * 1000) / 1000
    } else {
      this.basicPercentage = 0
    }

    //if (basic > 0) {
    //  this.hraPercentage = Math.round((hra / basic) * 100 * 1000) / 1000
    //} else {
    //  this.hraPercentage = 0
    //}
  }

  calculateInitialValues() {
    if (this.ctc <= 0) {
      this.form.patchValue({ basic: 0, pf: 0 }, { emitEvent: false })
      this.calculateSpecialAllowance()
      return
    }

    // Calculate raw values
    const rawBasic = (this.ctc * this.basicPercentage) / 100
    const basic = Math.ceil(rawBasic)

    //const rawHra = (basic * this.hraPercentage) / 100
    //const hra = Math.ceil(rawHra)

    let pf = 0

    if (this.payoutFrequency === PayoutFrequency.MONTHLY && this.complianceSettings?.pf) {
      pf = this.calculatePf()
    }

    this.form.patchValue({ basic: basic, pf: pf }, { emitEvent: false })
    this.calculateSpecialAllowance()
  }

  calculatePf(): number {
    let pf = 0
    const rawPf = (0.12 * this.ctc) / 1.12
    pf = Math.ceil(rawPf)

    // Apply capping if enabled - cap should be integer
    const intCap = Math.floor(this.annualPfCap)
    if (this.complianceSettings.employerCapping && pf > intCap) {
      pf = intCap
    }
    return pf
  }

  // Calculates the maximum integer value a field can take without making special allowance negative
  getMaxAllowedValue(fieldName: 'basic' | 'pf'): number {
    const currentBasic = this.form.get('basic')?.value ?? 0
    //const currentHra = this.form.get('hra')?.value ?? 0
    const currentPf = this.form.get('pf')?.value ?? 0
    const otherComponentsSum = this.getOtherComponentsSumInCtc() // Integers

    let fixedSum = 0
    switch (
      fieldName // Removed Hra calculation
    ) {
      case 'basic':
        fixedSum = Number(currentPf) + otherComponentsSum
        break
      case 'pf':
        fixedSum = Number(currentBasic) + otherComponentsSum
        break
    }

    // Floor the result to get the max *integer* allowed without exceeding CTC
    const maxValue = Math.floor(this.ctc - fixedSum)
    return Math.max(0, maxValue)
  }

  updateBasicPercentage() {
    const basic = this.form.get('basic')?.value || 0
    if (this.ctc > 0) {
      this.basicPercentage = Math.round((basic / this.ctc) * 100 * 1000) / 1000
    } else {
      this.basicPercentage = 0
    }
  }

  //updateHraPercentage() {
  //  const hra = this.form.get('hra')?.value || 0
  //  const basic = this.form.get('basic')?.value || 0
  //  if (basic > 0) {
  //    this.hraPercentage = Math.round((hra / basic) * 100 * 1000) / 1000
  //  } else {
  //    this.hraPercentage = 0
  //  }
  //}

  updateBasicAmount(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const percentage = inputElement.valueAsNumber
    if (isNaN(percentage) || percentage < 0) return

    this.basicPercentage = percentage
    const rawBasicAmount = (this.ctc * this.basicPercentage) / 100
    const basicCeiled = Math.ceil(rawBasicAmount)

    const maxBasic = this.getMaxAllowedValue('basic')
    if (basicCeiled > maxBasic) {
      this.form.get('basic')?.setValue(maxBasic)
      // Update percentage based on the capped integer value
      if (this.ctc > 0) {
        this.basicPercentage = Math.round((maxBasic / this.ctc) * 100 * 1000) / 1000
        inputElement.value = this.basicPercentage.toFixed(3)
      }
    } else {
      // Use setValue to trigger valueChanges listener with the integer value
      this.form.get('basic')?.setValue(basicCeiled)
    }
  }

  //updateHraAmount(event: Event) {
  //  const inputElement = event.target as HTMLInputElement
  //  const percentage = inputElement.valueAsNumber
  //  if (isNaN(percentage) || percentage < 0) return

  //  this.hraPercentage = percentage
  //  const basic = this.form.get('basic')?.value || 0
  //  const rawHraAmount = (basic * this.hraPercentage) / 100
  //  const hraCeiled = Math.ceil(rawHraAmount)

  //  const maxHra = this.getMaxAllowedValue('hra')

  //  if (hraCeiled > maxHra) {
  //    this.form.get('hra')?.setValue(maxHra)
  //    if (basic > 0) {
  //      this.hraPercentage = Math.round((maxHra / basic) * 100 * 1000) / 1000
  //      inputElement.value = this.hraPercentage.toFixed(3) // Update input display
  //    }
  //  } else {
  //    this.form.get('hra')?.setValue(hraCeiled)
  //  }
  //}

  // --- Calculation Helpers ---
  //calculateHraFromBasic() {
  //  const basic = this.form.get('basic')?.value || 0
  //  const rawHraAmount = (basic * this.hraPercentage) / 100
  //  const hraCeiled = Math.ceil(rawHraAmount)

  //  const maxHra = this.getMaxAllowedValue('hra')

  //  if (hraCeiled > maxHra) {
  //    this.form.get('hra')?.setValue(maxHra, { emitEvent: false })
  //    this.updateHraPercentage()
  //    this.calculateSpecialAllowance()
  //  } else {
  //    this.form.get('hra')?.setValue(hraCeiled, { emitEvent: false })
  //    this.calculateSpecialAllowance()
  //  }
  //}

  private getOtherComponentsSumInCtc(): number {
    return (
      this.addedComponentItems.controls
        .filter(ctrl => ctrl.get('info.isInCTC')?.value)
        .reduce((sum: number, item: AbstractControl) => {
          const contribution = item.get('employerContribution')?.value ?? 0
          return sum + Number(contribution)
        }, 0) || 0
    )
  }

  calculateSpecialAllowance() {
    const basic = this.form.get('basic')?.value || 0
    //const hra = this.form.get('hra')?.value || 0
    const pf = this.form.get('pf')?.value || 0
    const otherComponentsSumInCtc = this.getOtherComponentsSumInCtc()

    const totalAllocatedInCtc = Number(basic) + Number(pf) + otherComponentsSumInCtc
    //const totalAllocatedInCtc = Number(basic) + Number(hra) + Number(pf) + otherComponentsSumInCtc
    const specialAllowanceAmount = Math.floor(this.ctc - totalAllocatedInCtc)

    // Ensure special allowance isn't negative
    const finalSpecialAllowance = Math.max(0, specialAllowanceAmount)

    if (this.form.get('specialAllowance')?.value !== finalSpecialAllowance) {
      this.form.patchValue({ specialAllowance: finalSpecialAllowance }, { emitEvent: false })
    }

    this.form.updateValueAndValidity({ emitEvent: false })
  }
  // --- /Calculation Helpers ---

  openModel(modal: unknown, componentName = '') {
    this.addCompError = null // Clear previous error
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'md'
    })
    if (modalRef.componentInstance) modalRef.componentInstance.salaryComponentName = componentName
  }

  onSearchChange(): void {
    this.searchTerm$.next(this.searchTerm)
  }

  onClose() {
    this.selectedComponentItem = null
    this.employerContribution = 0
    this.employeeContribution = 0
    this.addCompError = null
    this.isEditing = false
    this.searchTerm = ''
    this.searchTerm$.next('')
    this.modalService.dismissAll()
  }

  // --- Getters for FormArray and added items ---
  get addedComponentItems(): FormArray {
    return this.form.get('otherComponents') as FormArray
  }
  get notInCtcComponents() {
    return this.addedComponentItems.controls.filter(item => !item.get('info.isInCTC')?.value)
  }
  get inCtcComponents() {
    return this.addedComponentItems.controls.filter(item => item.get('info.isInCTC')?.value)
  }

  isComponentItemAdded(item: SalaryComponent): boolean {
    return this.addedComponentItems.value.some(
      (added: { id?: string; name?: string }) => added.id === item.id || added.name === item.name // Check by ID or name
    )
  }

  onRadioChange(item: SalaryComponent) {
    this.addCompError = null
    this.selectedComponentItem = item
    this.isEditing = false // Selecting a new item is always "add" mode
    this.employerContribution = 0 // Reset contributions for new selection
    this.employeeContribution = 0
    //if (!this.isComponentItemAdded(item)) {
    //  this.selectedComponentItem = item
    //} else {
    //  this.selectedComponentItem = null
    //}
  }

  getAddedContributionOfEmployer(item: SalaryComponent): number {
    // Reads integer value from form
    const found = this.addedComponentItems.controls.find(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )
    return found ? (found.get('employerContribution')?.value ?? 0) : 0
  }
  getAddedContributionOfEmployee(item: SalaryComponent): number {
    // Reads integer value from form
    const found = this.addedComponentItems.controls.find(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )
    return found ? (found.get('employeeContribution')?.value ?? 0) : 0
  }

  startEditing(item: SalaryComponent): void {
    this.isEditing = true
    this.selectedComponentItem = item
    this.employerContribution = this.getAddedContributionOfEmployer(item)
    this.employeeContribution = this.getAddedContributionOfEmployee(item)
    this.addCompError = null
  }

  // --- Add/Remove Component Items ---
  addComponentItem(): void {
    this.addCompError = null

    if (!this.selectedComponentItem) {
      this.addCompError = 'Please select a component to add.'
      return
    }

    const employerContributionCeiled = Math.ceil(this.employerContribution ?? 0)
    const employeeContributionCeiled = Math.ceil(this.employeeContribution ?? 0)

    if (employerContributionCeiled < 0 || employeeContributionCeiled < 0) {
      this.addCompError = 'Contribution amounts cannot be negative.'
      return
    }

    // --- CTC Check with Integers ---
    const basic = this.form.get('basic')?.value || 0
    //const hra = this.form.get('hra')?.value || 0
    const pf = this.form.get('pf')?.value || 0
    let currentOtherComponentsSumInCtc = this.getOtherComponentsSumInCtc()

    // If editing, temporarily subtract the item's old contribution from the total for an accurate check
    if (this.isEditing) {
      const oldItemContribution = this.getAddedContributionOfEmployer(this.selectedComponentItem)
      currentOtherComponentsSumInCtc -= oldItemContribution
    }
    const newComponentEmployerContribution = this.selectedComponentItem.info?.isInCTC
      ? employerContributionCeiled
      : 0

    const potentialTotalAllocated =
      Number(basic) +
      //Number(hra) +
      Number(pf) +
      currentOtherComponentsSumInCtc +
      newComponentEmployerContribution

    if (potentialTotalAllocated > this.ctc) {
      const overAmount = potentialTotalAllocated - this.ctc
      this.addCompError = `Cannot add component. Adding ${this.selectedComponentItem.name} (${newComponentEmployerContribution}) would exceed CTC by ${overAmount.toFixed(0)}.` // Show integer over amount
      return
    }
    // --- End CTC Check ---

    // If we were editing, remove the old item from the form array.
    if (this.isEditing) {
      const index = this.addedComponentItems.controls.findIndex(
        ctrl => ctrl.value.id === this.selectedComponentItem!.id
      )
      if (index > -1) {
        this.addedComponentItems.removeAt(index)
      }
    }

    // Create the new item using the ceilinged contributions
    const newItemData: ExtendedSalaryComponent = {
      ...this.selectedComponentItem,
      employerContribution: employerContributionCeiled,
      employeeContribution: employeeContributionCeiled
    }
    const newItemFormGroup = this.createOtherComponentGroup(newItemData) // create group handles ceiling again, redundant but safe

    this.addedComponentItems.push(newItemFormGroup)

    // Reset modal inputs
    this.onClose()
  }

  // Remove component item by index
  removeComponentItem(index: number): void {
    if (index >= 0 && index < this.addedComponentItems.length) {
      this.addedComponentItems.removeAt(index)
    }
  }

  // Remove component item and update modal UI
  removeComponentItemFromModal(item: SalaryComponent): void {
    const index = this.addedComponentItems.controls.findIndex(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )

    if (index > -1) {
      this.removeComponentItem(index) // Reuse existing logic
    }

    // If the deleted item was the one selected for editing, reset the modal state.
    if (this.selectedComponentItem?.id === item.id) {
      this.selectedComponentItem = null
      this.isEditing = false
      this.employerContribution = 0
      this.employeeContribution = 0
    }
  }
}
